import React from 'react'
import {
  Building2,
  TrendingUp,
  Euro,
  Gauge,
  Clock,
  PackageOpen,
  Coins,
  CheckCircle,
} from 'lucide-react'
import { KPICard, KPICardData } from '@/components/shared/KPICard'

export interface OverviewKpi {
  value: number | null
  previousValue: number | null
  deltaPct: number | null
}

export interface OverviewKpis {
  activeProjects: OverviewKpi
  unitsListed: OverviewKpi
  sales: OverviewKpi
  inventoryValue: OverviewKpi
  avgSqmPrice: OverviewKpi
  medianTimeOnMarket: OverviewKpi
  monthsOfSupply: OverviewKpi
  sellThroughRate: OverviewKpi
}

interface Props {
  kpis: OverviewKpis
  periodLabel: string
}

function euro(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  if (value >= 1_000_000_000) return `€${(value / 1_000_000_000).toFixed(2)}B`
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `€${(value / 1_000).toFixed(0)}k`
  return `€${Math.round(value)}`
}

function int(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return Math.round(value).toLocaleString()
}

function pct(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${value.toFixed(1)}%`
}

function months(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return value.toFixed(1)
}

function days(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${Math.round(value)}d`
}

export function AnalyticsKPIRow({ kpis, periodLabel }: Props) {
  const deltaLabel = `vs prev ${periodLabel.toLowerCase().replace('last ', '')}`

  const cards: KPICardData[] = [
    {
      id: 'activeProjects',
      label: 'Active projects',
      value: int(kpis.activeProjects.value),
      icon: Building2,
      color: 'blue',
      detail: 'sold_out = false · status = active',
      info: {
        definition:
          'Count of developments currently on the market (not sold-out) and not archived. Represents the live supply side of your coverage universe.',
        formula: 'COUNT(DISTINCT project) WHERE sold_out = false AND status = "active"',
        calculation:
          'Uses the project table directly and honours the global filters bar (city, developer, broker, typology, etc.). A project flips out of this set when Monitoring marks it sold-out or when its last successful scrape ages beyond the archive threshold.',
        source: 'project',
      },
    },
    {
      id: 'unitsListed',
      label: 'Units listed',
      value: int(kpis.unitsListed.value),
      icon: PackageOpen,
      color: 'blue',
      detail: periodLabel,
      previousValue: kpis.unitsListed.previousValue,
      deltaPct: kpis.unitsListed.deltaPct,
      deltaLabel,
      info: {
        definition:
          'Number of units that entered the market during the selected period — a proxy for new supply/absorption opportunity.',
        formula:
          'COUNT(unit) WHERE listing_date BETWEEN period_start AND period_end',
        calculation:
          'Listing date is captured the first time a unit is seen with a listable status (available/reserved). The delta chip compares the selected period against the previous equal-length window (e.g. last 30d vs the 30d before).',
        source: 'unit.listing_date',
      },
    },
    {
      id: 'sales',
      label: 'Sales',
      value: int(kpis.sales.value),
      icon: CheckCircle,
      color: 'green',
      detail: periodLabel,
      previousValue: kpis.sales.previousValue,
      deltaPct: kpis.sales.deltaPct,
      deltaLabel,
      info: {
        definition:
          'Number of units that closed (transitioned to status = sold) within the selected period.',
        formula:
          'COUNT(unit) WHERE status = "sold" AND sales_date BETWEEN period_start AND period_end',
        calculation:
          'A sale is counted on sales_date (set when the scraper first observes the "sold" status). Reservations are excluded. Delta chip compares to the previous equal-length window.',
        source: 'unit.status, unit.sales_date',
      },
    },
    {
      id: 'inventoryValue',
      label: 'Available on the market',
      value: euro(kpis.inventoryValue.value),
      icon: Euro,
      color: 'purple',
      detail: 'Sum of current prices',
      info: {
        definition:
          'Total asking value of units currently on the market — GMV at risk / opportunity cost of unsold stock.',
        formula:
          'SUM(current_price) WHERE status IN ("available", "reserved") AND 500 ≤ sqm_price ≤ 25,000',
        calculation:
          'Point-in-time snapshot using the latest price seen by the scrapers. Excludes sold units and projects flagged sold-out. Units with null price are skipped. Respects the same outlier trim as Avg €/m² so both KPIs describe the same universe (toggle "Include outliers" in the filters to bypass the trim).',
        source: 'unit.current_price',
      },
    },
    {
      id: 'avgSqmPrice',
      label: 'Avg €/m² (trimmed)',
      value: kpis.avgSqmPrice.value == null ? '—' : `€${int(kpis.avgSqmPrice.value)}`,
      icon: Coins,
      color: 'purple',
      detail: 'Hard cap 500–25,000 €/m²',
      info: {
        definition:
          'Average price per square metre across available units after outlier trimming — the headline pricing benchmark for the current filters.',
        formula: 'AVG(current_price / private_gpa) WHERE 500 ≤ sqm_price ≤ 25,000',
        calculation:
          'Prices are divided by private gross area (private_gpa — the canonical Blue Tagus denominator) and then hard-capped to the 500–25,000 €/m² band to drop scraping errors and extreme outliers (ultra-luxury / garage rows). Units with null price or private area are excluded. Set Filters → Include outliers to bypass the trim.',
        source: 'unit.current_price, unit.private_gpa',
      },
    },
    {
      id: 'medianTimeOnMarket',
      label: 'Median days to sell',
      value: days(kpis.medianTimeOnMarket.value),
      icon: Clock,
      color: 'amber',
      detail: 'Sold in period',
      previousValue: kpis.medianTimeOnMarket.previousValue,
      deltaPct: kpis.medianTimeOnMarket.deltaPct,
      deltaLabel,
      invertDelta: true,
      info: {
        definition:
          'Typical time from listing to closing for units sold in the period. Median is used (not average) to resist outliers from very old stock.',
        formula:
          'MEDIAN(sales_date - listing_date) WHERE status = "sold" AND sales_date BETWEEN period_start AND period_end',
        calculation:
          'Requires both listing_date and sales_date on the unit. A decrease is good (green chip) — inverted delta semantics. Uses Postgres percentile_cont(0.5).',
        source: 'unit.listing_date, unit.sales_date',
      },
    },
    {
      id: 'monthsOfSupply',
      label: 'Months of supply',
      value: months(kpis.monthsOfSupply.value),
      icon: Gauge,
      color: 'amber',
      detail: 'Trailing 90d velocity',
      info: {
        definition:
          'Standard real-estate absorption indicator: how many months it would take to sell the current active inventory at the current sales pace. Balanced market ≈ 6 months.',
        formula: 'active_units / (sales_last_90d / 3)',
        calculation:
          'Velocity uses the trailing 90 days of sales divided by 3 to express monthly pace. Active units are those with status available or reserved. Displays 0 when there is no inventory, and "—" when there is inventory but no sales velocity (division by zero → undefined).',
        source: 'unit.status, unit.sales_date',
      },
    },
    {
      id: 'sellThroughRate',
      label: 'Sell-through rate',
      value: pct(kpis.sellThroughRate.value),
      icon: TrendingUp,
      color: 'green',
      detail: 'sold / (sold + active)',
      info: {
        definition:
          'Share of the historical supply that has already closed. A leading indicator of portfolio maturity / sell-out progress.',
        formula: 'sold / (sold + available + reserved) × 100',
        calculation:
          'Point-in-time ratio across all units visible under the current filters. 100% means the filtered scope has fully sold out; 0% means nothing has closed yet.',
        source: 'unit.status',
      },
    },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <KPICard key={c.id} card={c} />
      ))}
    </div>
  )
}
